// PDF da placa de QR para a GRÁFICA — não é o "imprimir" do navegador.
//
// A placa sai em vinil/PVC e depois é cortada, então o arquivo precisa de
// sangria, marcas de corte e área de segurança. Quem gera é o `pdf-generator`
// (tem o Chromium), e o QR sai em SVG: vinil é impresso grande, e o QR
// rasterizado que aparece na tela vira borrão — e QR borrado não lê.
import React, { useState } from 'react';
import { Alert, Button, Descriptions, Modal, Radio, Segmented, Space, Switch, Typography, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { api } from './shared';

const { Text } = Typography;

// Quantas placas cabem numa A4: passo = lado + 2×(sangria 3 + marca 5) + 2 mm
// de respiro. Mesma conta do template — se mudar lá, muda aqui.
function porFolha(lado: number) {
  const passo = lado + 16 + 2;
  return Math.max(1, Math.floor((210 - 12) / passo)) * Math.max(1, Math.floor((297 - 12) / passo));
}

export function ModalQrPlaca({ estacaoId, codigo, onClose }: {
  estacaoId: string; codigo: string; onClose: () => void;
}) {
  const [lado, setLado] = useState(100);
  const [modo, setModo] = useState<'a4' | 'individual'>('a4');
  const [guias, setGuias] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [pronto, setPronto] = useState<{ url: string; nome: string } | null>(null);

  const gerar = async () => {
    setGerando(true);
    try {
      const r = await api('op_estacao_qr_placa_pdf', {
        id: estacaoId, lado_mm: lado, modo, guias,
        // O backend só aceita os domínios da Hydrodata (allow-list) — placa de
        // vinil fica anos no campo e ninguém reconfere o destino depois de
        // instalada. Rodando em localhost, cai no domínio de produção.
        origem_url: window.location.origin,
      });
      const w = window.open(r.url, '_blank');
      if (w) { message.success(`${r.arquivo} gerado`); onClose(); return; }
      // Bloqueador de pop-up: a geração passa dos ~5 s de ativação transitória
      // do clique e o window.open volta null sem erro. O link devolve o clique.
      setPronto({ url: r.url, nome: r.arquivo });
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setGerando(false); }
  };

  return (
    <Modal open onCancel={onClose} width={560} title={<><FilePdfOutlined /> Placa de QR para a gráfica</>}
      footer={[
        <Button key="c" onClick={onClose}>Cancelar</Button>,
        <Button key="g" type="primary" loading={gerando} icon={<FilePdfOutlined />} onClick={gerar}>Gerar PDF</Button>,
      ]}>
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 14 }}>
        <Descriptions.Item label="Estação">{codigo}</Descriptions.Item>
        <Descriptions.Item label="Tamanho final">
          <Segmented value={lado} onChange={v => setLado(Number(v))}
            options={[{ value: 60, label: '60 mm' }, { value: 100, label: '100 mm' }, { value: 150, label: '150 mm' }]} />
        </Descriptions.Item>
        <Descriptions.Item label="Formato">
          <Radio.Group value={modo} onChange={e => setModo(e.target.value)}>
            <Space direction="vertical" size={2}>
              {/* Esta tela manda UMA estação, então a folha sai com uma placa e
                  o resto da grade em branco. Prometer "N por folha" fazia
                  esperar uma folha cheia e receber uma quase vazia. */}
              <Radio value="a4">Folha A4 (1 placa; cabem {porFolha(lado)} nesta folha)</Radio>
              <Radio value="individual">Uma página por placa ({lado + 16} × {lado + 16} mm)</Radio>
            </Space>
          </Radio.Group>
        </Descriptions.Item>
        <Descriptions.Item label="Guias de conferência">
          <Space>
            <Switch checked={guias} onChange={setGuias} size="small" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              desenha a linha de corte e a área de segurança — só para conferir, <b>não mandar assim</b> para a gráfica
            </Text>
          </Space>
        </Descriptions.Item>
      </Descriptions>
      {pronto && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }}
          message="Arquivo gerado"
          description={<span>
            O navegador bloqueou a abertura automática.{' '}
            <a href={pronto.url} target="_blank" rel="noreferrer">Abrir {pronto.nome}</a>
          </span>} />
      )}
      <Alert type="info" showIcon
        message="O que vai no arquivo"
        description={<span>
          Sangria de 3 mm (o fundo vaza além do corte), marcas de corte de 5 mm nos quatro cantos e
          área de segurança de 5 mm. O QR sai em vetor, com correção de erro alta — a placa fica no
          mato, pegando sol e arranhão, e ainda tem que ler.
        </span>} />
    </Modal>
  );
}
